import { useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const ORIGIN = "https://bebfdsx.cn";

export default function HotPage() {
  const [active, setActive] = useState("all");

  const list = useMemo(() => {
    const sorted = [...articles].sort((a, b) => b.hot - a.hot);
    return active === "all" ? sorted : sorted.filter((a) => a.categorySlug === active);
  }, [active]);

  useSeo({
    title: "吃瓜热度榜 - 实时娱乐话题排行｜黑料在线",
    description:
      "黑料在线吃瓜热度榜，按讨论量、阅读完成率与近 24 小时涨幅综合排序，覆盖综艺名场面、影视风向、舞台现场、红毯造型、音乐热歌与网络热梗，支持按专题筛选。",
    keywords: "黑料在线热榜,吃瓜排行榜,娱乐话题热度,黑料在线快速浏览",
    path: "/hot",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        url: `${ORIGIN}/hot`,
        name: "吃瓜热度榜",
        inLanguage: "zh-CN",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "吃瓜热榜", item: `${ORIGIN}/hot` },
          ],
        },
        mainEntity: {
          "@type": "ItemList",
          itemListElement: list.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${ORIGIN}/article/${a.id}`,
            name: a.title,
          })),
        },
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "吃瓜热榜" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
            吃瓜热度榜
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            分值综合讨论量、阅读完成率与近 24 小时涨幅，用来表示『最近有多热』，不代表内容的重要性排序。热榜每天
            09:00 与 18:00 各更新一次。
          </p>
        </section>

        <section className="w-full px-4 py-8 md:px-8 md:py-12">
          <div className="flex flex-wrap items-center gap-2 rounded-full border border-background-200 bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setActive("all")}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-label transition-colors cursor-pointer ${
                active === "all"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              全部话题
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setActive(c.slug)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-label transition-colors cursor-pointer ${
                  active === c.slug
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
            <div className="rounded-card border border-background-200 bg-background-50 px-5 py-2 md:px-6">
              {list.length === 0 ? (
                <p className="py-10 text-center text-sm text-foreground-500">该专题暂时还没有话题。</p>
              ) : (
                list.map((a, i) => <ArticleCard key={a.id} article={a} layout="row" rank={i + 1} />)
              )}
            </div>

            <aside className="flex flex-col gap-5">
              <div className="rounded-card border border-background-200 bg-secondary-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">当前筛选</h2>
                <p className="mt-2 text-sm text-foreground-700">
                  {active === "all"
                    ? "全部专题"
                    : categories.find((c) => c.slug === active)?.name}
                  ，共 {list.length} 条
                </p>
              </div>
              <div className="rounded-card border border-primary-200 bg-primary-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">怎么看更省时间</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  每条的摘要就是结论，热度分只做参考。先扫标题，再点开真正好奇的两三条即可。
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}